import { differenceInYears, format, isValid, parseISO } from "date-fns";
import { Loader } from "src/component";
import Swal from "sweetalert2";
import ReactDOMServer from "react-dom/server";
// import { validFileExtensions } from "./constants";
// import * as XLSX from "xlsx";

export const renderCurrency = (
  amount,
  currency = "NGN",
  positive = true,
  showSign = false
) => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    trailingZeroDisplay: "stripIfInteger",
    currencyDisplay: "narrowSymbol",
    signDisplay: showSign ? "always" : "auto",
  }).format(positive ? parseFloat(amount || 0) : parseFloat(amount || 0) * -1);
};

export const renderNumber = (amount, positive = true, showSign = false) => {
  return new Intl.NumberFormat("en-GB", {
    trailingZeroDisplay: "stripIfInteger",
    currencyDisplay: "narrowSymbol",
    signDisplay: showSign ? "always" : "auto",
  }).format(positive ? parseFloat(amount) : parseFloat(amount) * -1);
};

export const removeItemAtIndex = (array, indexToRemove) => {
  const copy = [...array];
  if (indexToRemove >= 0 && indexToRemove < copy.length) {
    copy.splice(indexToRemove, 1);
  }
  return copy;
};

export const setToStorage = (key, value) => {
  let storedValue = JSON.stringify(value);
  localStorage.setItem(key, storedValue);
};

export const getFromStorage = (key) => {
  let value = localStorage.getItem(key);
  return value ? JSON.parse(value) : null;
};

export const removeFromStorage = (key) => {
  localStorage.removeItem(key);
};

export const renderAlert = ({
  msg = "",
  title = "",
  type = "info",
  confirmButtonText = "OK",
  confirmButtonColor = "var(--primary-color)",
}) => {
  return new Promise((resolve) => {
    Swal.fire({
      text: msg,
      title: title,
      icon: type,
      confirmButtonText: confirmButtonText,
      confirmButtonColor: confirmButtonColor,
    }).then((result) => {
      resolve(result);
    });
  });
};

export const renderToastMsg = (message, icon = "error", time = 3000) => {
  return new Promise((resolve) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: time,
      timerProgressBar: true,
      width: "auto",
    });

    Toast.fire({
      icon: icon,
      title: message,
    }).then(() => {
      resolve();
    });
  });
};

export const renderConfirmDialogue = (
  message = "Are you sure?",
  text = "Are you sure you want to delete the selected case?",
  confirmButtonText = "Yes, delete it!"
) => {
  return new Promise((resolve, reject) => {
    Swal.fire({
      title: message,
      text: text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--primary-color)",
      cancelButtonColor: "#d33",
      confirmButtonText: confirmButtonText,
    }).then((result) => {
      if (result.isConfirmed) {
        resolve(true);
      } else {
        resolve(false);
      }
    });
  });
};


export const renderConfirmDialogueWithCustomLoader = async (
  message = "Are you sure?",
  text = "Are you sure you want to perform this action?",
  confirmButtonText = "Yes, continue!",
  onConfirm = null
) => {
  return Swal.fire({
    title: message,
    html: text, // allow custom HTML
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "var(--primary-color)",
    cancelButtonColor: "#d33",
    confirmButtonText: confirmButtonText,
    allowOutsideClick: () => !Swal.isLoading(),
    preConfirm: async () => {
      if (onConfirm) {
        // Replace modal content with your custom loader
        Swal.update({
          html: ReactDOMServer.renderToString(<Loader spin />),
          showCancelButton: false,
          confirmButtonText: "",
        });

        try {
          await onConfirm();
          return true;
        } catch (error) {
          Swal.update({
            html: text, // revert to original text on error
            showCancelButton: true,
            confirmButtonText: confirmButtonText,
          });
          Swal.showValidationMessage(`Request failed: ${error}`);
          return false;
        }
      }
    },
  }).then((result) => result.isConfirmed);
};


export const pluralize = (count, noun, suffix = "s") =>
  `${count} ${noun}${count !== 1 ? suffix : ""}`;

// export function isValidFileType(fileName, fileType) {
//   return (
//     fileName &&
//     validFileExtensions[fileType].indexOf(fileName.split(".").pop()) > -1
//   );
// }

// export const exportToExcel = (excelData, name) => {
//   const workbook = XLSX.utils.book_new();
//   const worksheet = XLSX.utils.aoa_to_sheet(excelData);

//   XLSX.utils.book_append_sheet(workbook, worksheet, "sheet");

//   const excelBuffer = XLSX.write(workbook, {
//     bookType: "xlsx",
//     type: "array",
//   });
//   const blob = new Blob([excelBuffer], {
//     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
//   });

//   const url = URL.createObjectURL(blob);
//   const link = document.createElement("a");
//   link.setAttribute("href", url);
//   link.setAttribute("download", name);
//   link.style.display = "none";
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
//   URL.revokeObjectURL(url);
// };

export const convertToCurrency = (data) => {
  if (typeof data !== "number") {
    // Handle the case where 'data' is not a valid number
    return "Invalid Amount";
  }

  const number = Number(data);
  const currency = number.toLocaleString("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
  });

  return currency;
};

export const renderStatusColor = (status) => {
  const lowerStatus = status.toLowerCase();
  switch (lowerStatus) {
    case "pending":
      return "bg-[#FFFEF5] text-[#817400]";
    case "progress":
    case "in progress":
    case "checked in":
    case "accepted":
      return "bg-[#EDFFEA] text-[#098545]";
    case "closed":
    case "rejected":
    case "checked out":
      return "bg-[#FFE9E9] text-[#C82C57]";
    case "draft":
      return "bg-[#FFF8ED] text-[#F0A432]";
    case "referred":
      return "bg-[#ECECEC] text-[#1E1E1E]";

    default:
      return "bg-[#ECECEC] text-[#1E1E1E]";
  }
};
export const assistanceStatusColor = (status, text = false) => {
  const lowerStatus = status.toLowerCase();
  if (text) {
    switch (lowerStatus) {
      case "in progress":
        return "text-[#0FAD91]";
      case "accepted":
        return "text-[#1E1E1E]";
      case "rejected":
        return "text-[#C82C57]";
      case "pending":
        return "text-[#F0A432]";
      case "completed":
        return "text-[#098545]";
      case "not completed":
      case "abandoned":
        return "text-[#980F7A]";

      default:
        return "";
    }
  } else {
    switch (lowerStatus) {
      case "in progress":
        return "bg-[#EDFFFC] text-[#0FAD91]";
      case "accepted":
        return "bg-[#ECECEC] text-[#1E1E1E]";
      case "rejected":
        return "bg-[#FFE9E9] text-[#C82C57]";
      case "pending":
        return "bg-[#FFF8ED] text-[#F0A432]";
      case "completed":
        return "bg-[#EDFFEA] text-[#098545]";
      case "not completed":
      case "abandoned":
        return "bg-[#FFEDF8] text-[#980F7A]";

      default:
        return "";
    }
  }
};

export const formatDate = (inputDateString) => {
  // Parse the input date string to a Date object
  const dateObject = parseISO(inputDateString);

  // Check if the parsed date is valid
  if (isValid(dateObject)) {
    // Format the date in the desired format
    const formattedDate = format(dateObject, "dd-MM-yyyy");
    return formattedDate;
  } else {
    // Handle the case of an invalid date
    return "Invalid Date";
  }
};

export const formatTime = (inputDateString) => {
  const createdAt = new Date(inputDateString);

  const time = createdAt.toLocaleTimeString();
  return time;
};

export const formatDateAndTime = (
  isoDate,
  options = { disableDate: false, disableTime: false }
) => {
  const date = new Date(isoDate);
  let formattedDate = "";
  let formattedTime = "";

  if (!options.disableDate) {
    formattedDate = format(date, "MMMM d, yyyy");
  }
  if (!options.disableTime) {
    formattedTime = format(date, "h:mm a");
  }

  return `${formattedDate}${!options.disableTime && !options.disableDate ? ", " : ""
    }${formattedTime}`;
};

export const generateUPC = () => {
  const randomDigits = Array.from({ length: 11 }, () =>
    Math.floor(Math.random() * 10)
  );

  const sumOdd = randomDigits
    .filter((_, index) => index % 2 === 0)
    .reduce((acc, digit) => acc + digit, 0);
  const sumEven = randomDigits
    .filter((_, index) => index % 2 === 1)
    .reduce((acc, digit) => acc + digit, 0);
  const total = sumOdd * 3 + sumEven;
  const checkDigit = (10 - (total % 10)) % 10;

  const upcCode = randomDigits.concat(checkDigit).join("");

  return upcCode;
};

export const renderStatusText = (type) => {
  switch (type) {
    case "credit":
      return "wallet top up";
    case "debit":
      return "withdrawal";
    default:
      return "";
  }
};

export const renderDate = (dateValue) => {
  const date = new Date(dateValue);

  const day = date.getUTCDate();
  const month = date.getUTCMonth() + 1; // Month is zero-based, so we add 1
  const year = date.getUTCFullYear();

  // Ensure that day and month have leading zeros if needed
  const formattedDay = day < 10 ? `0${day}` : day;
  const formattedMonth = month < 10 ? `0${month}` : month;

  const formattedDate = `${formattedDay}-${formattedMonth}-${year}`;

  return formattedDate;
};

export const renderNaira = (currency) => {
  const currencyFormatter = new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
  });

  const formattedValue = currencyFormatter.format(currency);

  return formattedValue;
};

export const formatValidationErrors = (errors) => {
  if (typeof errors === "string") {
    return errors;
  }

  if (typeof errors === "object" && errors !== null) {
    return Object.entries(errors)
      .map(
        ([key, messages]) =>
          `${key.replace(/[-_]/g, " ")}: ${messages.join("")}`
      )
      .join(" ");
  }

  return "Invalid error format";
};

export const filterDeliveryModesByTime = (modes) => {
  const currentTime = new Date();
  const currentHour = currentTime.getHours();

  if (currentHour >= 12) {
    return modes.filter((mode) => mode.value !== "same day");
  }

  return modes;
};

export const replaceHyphensAndUnderscores = (str) => {
  if (str && (str.includes("-") || str.includes("_"))) {
    return str.replace(/[-_]/g, " ");
  } else {
    return str;
  }
};

export const extractDropdownValues = (data, id, value = false) => {
  const dropdown = data?.find((item) => {
    return item?.id === id;
  });
  if (!dropdown) return [];

  return dropdown?.dropdowns.map(({ id, name }) => ({
    value: value ? name : id,
    label: name,
  }));
};

export const extractLGAData = (data, value = false) => {
  return data
    .filter((item) => item.slug.endsWith("-lga"))
    .map((item) => ({
      value: value
        ? item.name.replace(/\s*-?\s*LGA$/, "").toLowerCase()
        : item.id,
      label: item.name.replace(/\s*-?\s*LGA$/, ""), // Remove "- LGA" suffix
    }))
    .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically by label
};

export const extractSelectDropdownValues = (data, id) => {
  const dropdown = data?.find((item) => {
    return item?.id === id;
  });
  if (!dropdown) return [];

  return dropdown?.dropdowns.map(({ id, name }) => ({
    value: name,
    label: name,
  }));
};

export const ordinalSuffix = (number) => {
  const j = number % 10;
  const k = number % 100;
  if (j === 1 && k !== 11) {
    return `${number}st`;
  }
  if (j === 2 && k !== 12) {
    return `${number}nd`;
  }
  if (j === 3 && k !== 13) {
    return `${number}rd`;
  }
  return `${number}th`;
};

export function generateYearOptions() {
  const currentYear = new Date().getFullYear();
  const startYear = 1980;
  const yearRange = currentYear - startYear + 1;

  const yearOptions = Array.from({ length: yearRange }, (_, index) => {
    const year = startYear + index;

    return {
      value: year.toString(),
      label: year.toString(),
    };
  });

  return yearOptions;
}

export function removeAfterHyphen(str) {
  const parts = str?.split("-");
  return parts[0];
}

export const handleDownloadFile = (filePath, filename) => {
  const anchorElement = document.createElement("a");
  anchorElement.href = filePath;
  anchorElement.download = filename;
  anchorElement.style.display = "none";
  document.body.appendChild(anchorElement);
  anchorElement.click();
  document.body.removeChild(anchorElement);
};

export const convertJSONToCSV = (jsonData, fileName) => {
  const headers = Object.keys(jsonData[0]);
  const csvData = [
    headers.join(","),
    ...jsonData.map((item) => headers.map((header) => item[header]).join(",")),
  ].join("\n");

  const blob = new Blob([csvData], { type: "text/csv" });
  const link = document.createElement("a");

  link.href = window.URL.createObjectURL(blob);
  link.setAttribute("download", fileName + ".csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const calculateAgeFromDateOfBirth = (dob) => {
  // Check if the DOB is provided
  if (!dob) {
    return null;
  }

  const dobDate = parseISO(dob);
  // Calculate the difference in years between current date and DOB
  const age = differenceInYears(new Date(), dobDate);
  return age;
};

export const generateRandomString = (length) => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

export const getInitials = (name) => {
  const names = name.split(" ");
  const initials = names.map((name) => name.charAt(0).toUpperCase());
  return initials.join("");
};

export const formatCustomDate = (dateString) => {
  // Parse the ISO date string to a Date object
  const date = parseISO(dateString);

  const formattedDate = format(date, "d, MMMM, yyyy");

  return formattedDate;
};